<script setup>
/**
 * One broadcast in the list.
 *
 * Same row grammar as `TaskCard` and `AttentionRow` — **[mark] [what · numbers] [state]
 * [go]** — so the eye finds the control at the same place whatever the row says and
 * whichever direction its text runs.
 *
 * WHAT THE NUMBERS SAY, AND WHY THEY ARE A FRACTION. `sentCount` alone is a number that
 * cannot be read: 640 is either most of the way through or barely started depending on a
 * total that is somewhere else on the row. "{sent} of {total} sent" is one fact and it is
 * the one an owner is actually asking. The other three numbers — on-the-way, skipped and
 * did-not-arrive — are only rendered when they are non-zero, because "0 skipped" is a
 * reassurance nobody asked for taking the place of nothing at all.
 *
 * WHY "ON THE WAY" IS ON THIS ROW AT ALL, and why it is not an optional nicety. A run
 * finalises as soon as no recipient is still queued or sending — which is normally
 * BEFORE the first delivery callback arrives, so most of it sits in `pendingCount`
 * (dispatched, provider silent). `sentCount` used to include those; now it does not, and
 * a row that printed only the fraction would read "4 of 100 sent" on a send where
 * nothing whatsoever had gone wrong, with ninety-six recipients unaccounted for. The
 * fragment closes the total: sent + on-the-way + did-not-arrive + skipped IS the
 * recipient count.
 *
 * IT IS NOT A FAILURE AND MUST NEVER READ AS ONE. These messages were accepted; the only
 * thing missing is a receipt. The wording is the same three words used for the tile on
 * the detail page and for the `accepted` chip in the breakdown, deliberately — one
 * bucket, one name, or an owner comparing two screens is counting the same messages
 * twice.
 */
import { computed } from 'vue';

import { frontendURL } from 'dashboard/helper/URLHelper';

import BroadcastStatusChip from './BroadcastStatusChip.vue';
import { useWasloI18n } from '../../i18n.js';
import { relativeTime } from '../../helpers/relative-time.js';
import { toIntlLocale } from '../../helpers/intl.js';

const props = defineProps({
  /** A row from `wasloApi.getBroadcasts`. */
  broadcast: { type: Object, required: true },
  accountId: { type: Number, default: null },
});

const { t, locale } = useWasloI18n();

const num = value =>
  new Intl.NumberFormat(toIntlLocale(locale.value)).format(value);

/** The accent means "money is leaving right now" on this screen, and only then. */
const mark = computed(() =>
  props.broadcast.status === 'running'
    ? { tile: 'bg-n-blue-3', icon: 'text-n-blue-10' }
    : { tile: 'bg-n-alpha-2', icon: 'text-n-slate-10' }
);

const counts = computed(() => {
  const b = props.broadcast;
  const parts = [
    t('WASLO.BROADCASTS.ROW.SENT', {
      sent: num(b.sentCount),
      total: num(b.recipientCount),
    }),
  ];
  if (b.pendingCount > 0) {
    parts.push(
      t('WASLO.BROADCASTS.ROW.PENDING', { count: num(b.pendingCount) })
    );
  }
  if (b.excludedCount > 0) {
    parts.push(
      t('WASLO.BROADCASTS.ROW.SKIPPED', { count: num(b.excludedCount) })
    );
  }
  if (b.failedCount > 0) {
    parts.push(t('WASLO.BROADCASTS.ROW.FAILED', { count: num(b.failedCount) }));
  }
  return parts.join(' · ');
});

const when = computed(() => {
  if (!props.broadcast.launchedAt) return t('WASLO.BROADCASTS.ROW.NOT_STARTED');
  const ago = relativeTime(props.broadcast.launchedAt, locale.value, {
    justNow: t('WASLO.BROADCASTS.ROW.JUST_NOW'),
  });
  return ago ? t('WASLO.BROADCASTS.ROW.STARTED', { when: ago }) : '';
});

const to = computed(() =>
  props.accountId === null
    ? null
    : frontendURL(
        `accounts/${props.accountId}/campaigns/broadcasts/${props.broadcast.id}`
      )
);
</script>

<template>
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
    :data-waslo-broadcast="broadcast.id"
  >
    <span
      class="grid rounded-lg shrink-0 place-items-center size-9"
      :class="mark.tile"
      aria-hidden="true"
    >
      <span class="size-4 i-lucide-megaphone" :class="mark.icon" />
    </span>

    <div class="flex flex-col min-w-0 gap-1">
      <!--
        `<bdi>` inside a plain paragraph, never `dir="auto"` on the paragraph: the owner
        names these in Arabic, and `dir="auto"` on the block would fling the name to the far
        edge of the row while the numbers under it stayed at the other one.
      -->
      <p
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        data-waslo-broadcast-title
      >
        <bdi>{{ broadcast.title }}</bdi>
      </p>
      <p class="mb-0 text-sm text-n-slate-11" data-waslo-broadcast-counts>
        {{ counts }}
      </p>
      <p v-if="when" class="mb-0 text-xs text-n-slate-10">{{ when }}</p>
    </div>

    <div class="flex items-center gap-3 shrink-0 ms-0 sm:ms-auto">
      <BroadcastStatusChip :status="broadcast.status" />
      <router-link
        v-if="to"
        :to="to"
        class="inline-flex items-center h-8 gap-1 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4"
        data-waslo-broadcast-open
      >
        {{ t('WASLO.BROADCASTS.ROW.OPEN') }}
      </router-link>
    </div>
  </li>
</template>
